import React, { useState, useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { Plus, Trash2, Link2, IndianRupee, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';

const TASK_TYPES = ['QC', 'QC_MT', 'Trans', 'Tcue', 'Proof', 'QC_AS', 'Rfrm_S', 'Rfrm_C', 'Frcd_Cr', 'Metadata_Create', 'First Translation', 'Other'];
const RATE_TYPES = [
  { value: 'per_minute', label: 'Per Minute (₹/min)' },
  { value: 'per_word', label: 'Per Word (₹/word)' },
  { value: 'flat', label: 'Flat Rate (₹)' },
];

// Searchable input with dropdown suggestions
const SearchInput = ({ value, onChange, placeholder, options, loading: extLoading }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = (options || []).filter(o => {
    if (!value) return true;
    const label = typeof o === 'string' ? o : o.label || '';
    return label.toLowerCase().includes(value.toLowerCase());
  }).slice(0, 15);

  return (
    <div ref={ref} className="relative">
      <Input
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
      />
      {open && filtered.length > 0 && (
        <div className="absolute z-50 mt-1 w-full max-h-48 overflow-y-auto bg-popover border rounded-md shadow-lg">
          {filtered.map((o, i) => {
            const val = typeof o === 'string' ? o : o.value;
            const label = typeof o === 'string' ? o : o.label;
            return (
              <button key={i} onClick={() => { onChange(val); setOpen(false); }}
                className="w-full text-left px-3 py-1.5 text-sm hover:bg-muted truncate">
                {label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Title search with API
const TitleSearchInput = ({ value, onChange }) => {
  const [query, setQuery] = useState(value || '');
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => { setQuery(value || ''); }, [value]);
  useEffect(() => {
    if (query.length < 1) { setOptions([]); return; }
    const t = setTimeout(async () => {
      try { const res = await api.get(`/distinct-titles?q=${encodeURIComponent(query)}`); setOptions(res.data); } catch (e) {}
    }, 300);
    return () => clearTimeout(t);
  }, [query]);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      <Input value={query} onChange={(e) => { setQuery(e.target.value); setOpen(true); onChange(e.target.value); }}
        onFocus={() => setOpen(true)} placeholder="Type to search project titles..." />
      {open && options.length > 0 && (
        <div className="absolute z-50 mt-1 w-full max-h-48 overflow-y-auto bg-popover border rounded-md shadow-lg">
          {options.slice(0, 20).map(t => (
            <button key={t} onClick={() => { onChange(t); setQuery(t); setOpen(false); }}
              className="w-full text-left px-3 py-1.5 text-sm hover:bg-muted truncate">{t}</button>
          ))}
        </div>
      )}
    </div>
  );
};

const AddTitleInput = ({ onAdd }) => {
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (query.length < 1) { setOptions([]); return; }
    const t = setTimeout(async () => {
      try { const res = await api.get(`/distinct-titles?q=${encodeURIComponent(query)}`); setOptions(res.data); } catch (e) {}
    }, 300);
    return () => clearTimeout(t);
  }, [query]);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      <Input value={query} onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)} placeholder="Type to search and add project title..." />
      {open && options.length > 0 && (
        <div className="absolute z-50 mt-1 w-full max-h-48 overflow-y-auto bg-popover border rounded-md shadow-lg">
          {options.slice(0, 20).map(t => (
            <button key={t} onClick={() => { onAdd(t); setQuery(''); setOpen(false); }}
              className="w-full text-left px-3 py-1.5 text-sm hover:bg-muted truncate">{t}</button>
          ))}
        </div>
      )}
    </div>
  );
};

const MapPro = () => {
  const { user } = useOutletContext();
  const [activeTab, setActiveTab] = useState('mappings');
  const [mappings, setMappings] = useState([]);
  const [priceMappings, setPriceMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showPriceAdd, setShowPriceAdd] = useState(false);
  const [editPrice, setEditPrice] = useState(null);

  // Data for search
  const [translators, setTranslators] = useState([]);
  const [gtsEmails, setGtsEmails] = useState([]);

  // Mapping form
  const [selTranslator, setSelTranslator] = useState('');
  const [selTranslatorId, setSelTranslatorId] = useState('');
  const [selGtsEmail, setSelGtsEmail] = useState('');

  // Price form
  const [priceTranslator, setPriceTranslator] = useState('');
  const [priceTranslatorId, setPriceTranslatorId] = useState('');
  const [priceGtsEmail, setPriceGtsEmail] = useState('');
  const [priceTitle, setPriceTitle] = useState('');
  const [priceTaskType, setPriceTaskType] = useState('');
  const [priceRateType, setPriceRateType] = useState('per_minute');
  const [priceRate, setPriceRate] = useState('');

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const [mapRes, usersRes, gtsRes, priceRes] = await Promise.all([
        api.get('/map-pro'),
        api.get('/translators').catch(() => ({ data: [] })),
        api.get('/gts-pro-emails'),
        api.get('/price-mappings'),
      ]);
      setMappings(mapRes.data);
      setPriceMappings(priceRes.data);
      const tList = (usersRes.data || []).map(p => ({
        user_id: p.user_id, name: p.full_name || p.user_id, email: p.personal_email || ''
      }));
      setTranslators(tList);
      setGtsEmails(gtsRes.data);
    } catch (e) { toast.error('Failed to load data'); }
    setLoading(false);
  };

  // Translator search options
  const translatorOptions = translators.map(t => ({ value: t.user_id, label: `${t.name} (${t.email})` }));
  // GTS email search options
  const gtsEmailOptions = gtsEmails.filter(e => e.status === 'active').map(e => ({
    value: e.email, label: `${e.email} — ${(e.project_titles || []).join(', ') || 'No projects'}`
  }));

  const handleCreateMapping = async () => {
    const tId = selTranslatorId || translators.find(t => `${t.name} (${t.email})`.includes(selTranslator))?.user_id;
    if (!tId || !selGtsEmail) { toast.error('Select both translator and GTS email'); return; }
    try {
      await api.post('/map-pro', { translator_id: tId, gts_email: selGtsEmail });
      toast.success('Mapping created');
      setShowAdd(false); setSelTranslator(''); setSelTranslatorId(''); setSelGtsEmail('');
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleDeleteMapping = async (id) => {
    if (!confirm('Remove?')) return;
    try { await api.delete(`/map-pro/${id}`); toast.success('Removed'); loadAll(); } catch (e) { toast.error('Failed'); }
  };

  // Edit mapping projects
  const [editMappingEmail, setEditMappingEmail] = useState(null);
  const [editMappingTitles, setEditMappingTitles] = useState([]);
  const [showEditMapping, setShowEditMapping] = useState(false);

  const openEditMapping = (m) => {
    const gts = gtsEmails.find(e => e.email === m.gts_email);
    setEditMappingEmail(gts || { email: m.gts_email, email_id: null, project_titles: m.project_titles || [] });
    setEditMappingTitles(m.project_titles || []);
    setShowEditMapping(true);
  };

  const handleSaveMappingProjects = async () => {
    if (!editMappingEmail?.email_id) { toast.error('Cannot find GTS email entry'); return; }
    try {
      await api.put(`/owner/gts-pro-emails/${editMappingEmail.email_id}`, { project_titles: editMappingTitles });
      toast.success('Mapped projects updated');
      setShowEditMapping(false);
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
  };

  const resetPriceForm = () => {
    setPriceTranslator(''); setPriceTranslatorId(''); setPriceGtsEmail('');
    setPriceTitle(''); setPriceTaskType(''); setPriceRateType('per_minute'); setPriceRate('');
    setEditPrice(null);
  };

  const handleSavePrice = async () => {
    const tId = priceTranslatorId || translators.find(t => `${t.name} (${t.email})`.includes(priceTranslator))?.user_id;
    if (!tId || !priceRate) { toast.error('Translator and rate required'); return; }
    try {
      const payload = {
        translator_id: tId, gts_email: priceGtsEmail,
        project_title: priceTitle, task_type: priceTaskType,
        rate_type: priceRateType, rate_inr: parseFloat(priceRate)
      };
      if (editPrice) {
        await api.put(`/price-mappings/${editPrice.price_id}`, payload);
        toast.success('Updated');
      } else {
        await api.post('/price-mappings', payload);
        toast.success('Created');
      }
      setShowPriceAdd(false); resetPriceForm(); loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleDeletePrice = async (id) => {
    if (!confirm('Delete?')) return;
    try { await api.delete(`/price-mappings/${id}`); toast.success('Deleted'); loadAll(); } catch (e) { toast.error('Failed'); }
  };

  const openEditPrice = (p) => {
    setEditPrice(p);
    const t = translators.find(t => t.user_id === p.translator_id);
    setPriceTranslator(t ? `${t.name} (${t.email})` : p.translator_id);
    setPriceTranslatorId(p.translator_id);
    setPriceGtsEmail(p.gts_email || '');
    setPriceTitle(p.project_title || ''); setPriceTaskType(p.task_type || '');
    setPriceRateType(p.rate_type || 'per_minute'); setPriceRate(String(p.rate_inr || ''));
    setShowPriceAdd(true);
  };

  const getTranslatorName = (id) => translators.find(t => t.user_id === id)?.name || id;

  if (loading) return <DashboardLayout user={user}><div className="flex items-center justify-center h-96"><div className="w-12 h-12 border-4 border-violet-500 border-t-transparent rounded-full animate-spin" /></div></DashboardLayout>;

  return (
    <DashboardLayout user={user}>
      <div className="space-y-6">
        <div><h1 className="text-4xl font-heading font-bold accent-gradient" data-testid="map-pro-title">MapPro</h1><p className="text-muted-foreground">Translator mappings and pricing</p></div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="mappings" className="flex items-center gap-1"><Link2 className="w-4 h-4" /> Mappings ({mappings.length})</TabsTrigger>
            <TabsTrigger value="pricing" className="flex items-center gap-1"><IndianRupee className="w-4 h-4" /> Price Mapping ({priceMappings.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="mappings" className="space-y-4">
            <div className="flex justify-end"><Button size="sm" onClick={() => setShowAdd(true)} data-testid="add-mapping-btn"><Plus className="w-4 h-4 mr-2" /> Add Mapping</Button></div>
            <Card><CardContent className="p-0">
              {mappings.length === 0 ? <div className="py-12 text-center"><Link2 className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" /><p className="text-muted-foreground">No mappings yet</p></div> : (
              <div className="overflow-x-auto"><Table><TableHeader><TableRow>
                <TableHead className="w-10">#</TableHead><TableHead>Translator</TableHead><TableHead>GTS Pro Email</TableHead><TableHead>Status</TableHead><TableHead>Mapped Projects</TableHead><TableHead className="w-16 text-center">Action</TableHead>
              </TableRow></TableHeader><TableBody>
                {mappings.map((m, i) => (
                  <TableRow key={m.mapping_id}>
                    <TableCell className="text-muted-foreground text-xs">{i + 1}</TableCell>
                    <TableCell><p className="font-medium text-sm">{m.translator_name || m.translator_id}</p><p className="text-xs text-muted-foreground">{m.translator_email}</p></TableCell>
                    <TableCell className="font-mono text-sm">{m.gts_email}</TableCell>
                    <TableCell>{m.gts_status === 'active' ? <Badge className="bg-green-100 text-green-700 text-xs">Active</Badge> : <Badge className="bg-gray-100 text-gray-500 text-xs">{m.gts_status || '?'}</Badge>}</TableCell>
                    <TableCell><div className="flex gap-1 flex-wrap max-w-[300px]">{(m.project_titles || []).map((t, j) => <Badge key={j} variant="outline" className="text-[10px]">{t}</Badge>)}{(!m.project_titles || !m.project_titles.length) && <span className="text-xs text-muted-foreground">None — assign in GTS Pro Emails</span>}</div></TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEditMapping(m)} className="h-7 w-7 p-0"><Pencil className="w-3.5 h-3.5" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteMapping(m.mapping_id)} className="h-7 w-7 p-0 text-destructive"><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody></Table></div>)}
            </CardContent></Card>
          </TabsContent>

          <TabsContent value="pricing" className="space-y-4">
            <div className="flex justify-end"><Button size="sm" onClick={() => { resetPriceForm(); setShowPriceAdd(true); }} data-testid="add-price-btn"><Plus className="w-4 h-4 mr-2" /> Add Price</Button></div>
            <Card><CardContent className="p-0">
              {priceMappings.length === 0 ? <div className="py-12 text-center"><IndianRupee className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" /><p className="text-muted-foreground">No price mappings yet</p></div> : (
              <div className="overflow-x-auto"><Table><TableHeader><TableRow>
                <TableHead className="w-10 text-center">#</TableHead><TableHead className="text-center">Translator</TableHead><TableHead className="text-center">GTS Email</TableHead><TableHead className="text-center">Project Title</TableHead><TableHead className="text-center">Task</TableHead><TableHead className="text-center">Rate Type</TableHead><TableHead className="text-center">Rate (₹)</TableHead><TableHead className="w-20 text-center">Actions</TableHead>
              </TableRow></TableHeader><TableBody>
                {priceMappings.map((p, i) => (
                  <TableRow key={p.price_id}>
                    <TableCell className="text-center text-muted-foreground text-xs">{i + 1}</TableCell>
                    <TableCell className="text-center text-sm">{getTranslatorName(p.translator_id)}</TableCell>
                    <TableCell className="text-center text-xs font-mono">{p.gts_email || '—'}</TableCell>
                    <TableCell className="text-center text-sm">{p.project_title || <span className="text-muted-foreground">Any</span>}</TableCell>
                    <TableCell className="text-center text-sm">{p.task_type || <span className="text-muted-foreground">Any</span>}</TableCell>
                    <TableCell className="text-center text-xs"><Badge variant="outline">{RATE_TYPES.find(r => r.value === p.rate_type)?.label || p.rate_type}</Badge></TableCell>
                    <TableCell className="text-center font-semibold text-green-700">₹{p.rate_inr}</TableCell>
                    <TableCell className="text-center"><div className="flex items-center justify-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEditPrice(p)} className="h-7 w-7 p-0"><Pencil className="w-3.5 h-3.5" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDeletePrice(p.price_id)} className="h-7 w-7 p-0 text-destructive"><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div></TableCell>
                  </TableRow>
                ))}
              </TableBody></Table></div>)}
            </CardContent></Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Create Mapping Dialog — all searchable */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent><DialogHeader><DialogTitle><Link2 className="w-5 h-5 inline mr-2" />Create Mapping</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1"><Label>Translator</Label>
              <SearchInput value={selTranslator} onChange={(v) => {
                setSelTranslator(v);
                const match = translators.find(t => `${t.name} (${t.email})` === v);
                if (match) setSelTranslatorId(match.user_id);
              }} placeholder="Type translator name..." options={translatorOptions} />
            </div>
            <div className="space-y-1"><Label>GTS Pro Email</Label>
              <SearchInput value={selGtsEmail} onChange={setSelGtsEmail} placeholder="Type GTS email..." options={gtsEmailOptions} />
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button><Button onClick={handleCreateMapping}>Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Price Mapping Dialog — all searchable */}
      <Dialog open={showPriceAdd} onOpenChange={(o) => { if (!o) { setShowPriceAdd(false); resetPriceForm(); } }}>
        <DialogContent><DialogHeader><DialogTitle><IndianRupee className="w-5 h-5 inline mr-2" />{editPrice ? 'Edit' : 'Add'} Price Mapping</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1"><Label>Translator</Label>
              <SearchInput value={priceTranslator} onChange={(v) => {
                setPriceTranslator(v);
                const match = translators.find(t => `${t.name} (${t.email})` === v);
                if (match) setPriceTranslatorId(match.user_id);
              }} placeholder="Type translator name..." options={translatorOptions} />
            </div>
            <div className="space-y-1"><Label>GTS Pro Email (linked to project)</Label>
              <SearchInput value={priceGtsEmail} onChange={setPriceGtsEmail} placeholder="Type GTS email..." options={gtsEmailOptions} />
            </div>
            <div className="space-y-1"><Label>Project Title</Label>
              <TitleSearchInput value={priceTitle} onChange={setPriceTitle} />
            </div>
            <div className="space-y-1"><Label>Task Type</Label>
              <SearchInput value={priceTaskType} onChange={setPriceTaskType} placeholder="Type task type..." options={TASK_TYPES} />
            </div>
            <div className="space-y-1"><Label>Rate Type</Label>
              <Select value={priceRateType} onValueChange={setPriceRateType}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{RATE_TYPES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="space-y-1"><Label>Rate (₹ INR)</Label>
              <Input type="number" value={priceRate} onChange={(e) => setPriceRate(e.target.value)} placeholder="15.00" data-testid="price-rate-input" />
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => { setShowPriceAdd(false); resetPriceForm(); }}>Cancel</Button><Button onClick={handleSavePrice}>{editPrice ? 'Update' : 'Create'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Mapping Projects Dialog */}
      <Dialog open={showEditMapping} onOpenChange={setShowEditMapping}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle><Pencil className="w-5 h-5 inline mr-2" />Edit Mapped Projects — {editMappingEmail?.email}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Search and add project titles (Main Titles from Client Projects/Billings)</p>
            <div className="flex flex-wrap gap-1 min-h-[32px]">
              {editMappingTitles.map(t => (
                <Badge key={t} variant="secondary" className="text-xs gap-1">
                  {t}
                  <button onClick={() => setEditMappingTitles(prev => prev.filter(x => x !== t))} className="ml-1 hover:text-destructive">&times;</button>
                </Badge>
              ))}
            </div>
            <AddTitleInput onAdd={(t) => { if (t && !editMappingTitles.includes(t)) setEditMappingTitles(prev => [...prev, t]); }} />
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowEditMapping(false)}>Cancel</Button><Button onClick={handleSaveMappingProjects}>Save Projects</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default MapPro;
